import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSpring, animated, SpringValue } from "@react-spring/web";
import { LayerGroup, LayerGroupName } from "types/dataModel";
import { ReactComponent as LinkSvg } from "assets/link-icon.svg";
import downloads from "data/downloads";
import SearchBar, { Bounds } from "panels/home-info-panel/search-bar";
import { Icon } from "@iconify/react";
import {
  useLayerBounceContext,
  LayerSelectionFrom,
  LAYER_BOUNCE_FLASH_TIMEOUT,
  LAYER_BOUNCE_OPEN_TIMEOUT,
} from "layers/layer-bounce";
import "panels/layer-selection/layer-selection.css";
import QuickExplore from "panels/quick-explore";
import { useAnalyticsHandler } from "hooks/useAnalytics";
import { useMobileContext } from "contexts/MobileContext";

type LayerSelectionProps = {
  layerGroups: Record<LayerGroupName, LayerGroup>;
  selectedLayer: LayerGroupName;
  setSelectedLayer: (layer: LayerGroupName) => void;
  setBounds: (bounds: Bounds) => void;
};

const ICON_CONTAINER_INITIAL_WIDTH = 55;
const ICON_INITIAL_SIZE = 30;
const ICON_HOVER_SIZE = 35;

export const LayerSelection: React.FC<LayerSelectionProps> = ({
  layerGroups,
  selectedLayer,
  setSelectedLayer,
  setBounds,
}) => {
  const layerSelectedHandler = useAnalyticsHandler("Layer Selected");
  const linkOpenedHandler = useAnalyticsHandler("Link Opened");
  const { layerGroupSelectedFrom, setLayerGroupSelectedFrom } =
    useLayerBounceContext();
  const layerSelectionContainer = useRef<HTMLDivElement>(null);
  const activeLayerButton = useRef<HTMLButtonElement>(null);
  const [containerSprings, containerApi] = useSpring(() => ({
    from: { width: ICON_CONTAINER_INITIAL_WIDTH },
  }));
  const [iconSprings, iconApi] = useSpring(() => ({
    from: { width: ICON_INITIAL_SIZE, height: ICON_INITIAL_SIZE },
  }));

  // Fixed spring configuration for SEARCH item to prevent animation interference
  const [searchIconSprings] = useSpring(() => ({
    from: { width: ICON_INITIAL_SIZE, height: ICON_INITIAL_SIZE },
    config: { duration: 0 }, // No animation
  }));

  // Mobile context
  const { isTouch, isLayerSelectionExpanded, setIsLayerSelectionExpanded } =
    useMobileContext();

  useEffect(() => {
    if (isTouch && isLayerSelectionExpanded) {
      // On mobile, let CSS handle the width (75vw), only animate icons
      iconApi.start({ width: ICON_HOVER_SIZE, height: ICON_HOVER_SIZE });
    } else if (isTouch && !isLayerSelectionExpanded) {
      // On mobile collapsed, let CSS handle the width transition, only animate icons
      iconApi.start({ width: ICON_INITIAL_SIZE, height: ICON_INITIAL_SIZE });
    } else {
      // Desktop behavior
      containerApi.start({ width: ICON_CONTAINER_INITIAL_WIDTH });
      iconApi.start({ width: ICON_INITIAL_SIZE, height: ICON_INITIAL_SIZE });
    }
  }, [containerApi, iconApi, isTouch, isLayerSelectionExpanded]);

  const handleIconHover = useCallback(() => {
    if (!isTouch) {
      containerApi.start({ width: activeLayerButton.current?.scrollWidth });
      iconApi.start({ width: ICON_HOVER_SIZE, height: ICON_HOVER_SIZE });
    }
  }, [containerApi, iconApi, isTouch]);

  const handleIconUnhover = useCallback(() => {
    if (!isTouch) {
      containerApi.start({ width: ICON_CONTAINER_INITIAL_WIDTH });
      iconApi.start({ width: ICON_INITIAL_SIZE, height: ICON_INITIAL_SIZE });
    }
  }, [containerApi, iconApi, isTouch]);

  useEffect(() => {
    if (layerGroupSelectedFrom === LayerSelectionFrom.breadcrumb) {
      handleIconHover();
      setTimeout(() => {
        handleIconUnhover();
      }, LAYER_BOUNCE_OPEN_TIMEOUT); //This is the length of time to open the panel
      setTimeout(() => {
        setLayerGroupSelectedFrom(LayerSelectionFrom.reset);
      }, LAYER_BOUNCE_FLASH_TIMEOUT); //This should correspond to whatever the CSS animation time is, or it will be cut short
    }
  }, [
    handleIconHover,
    handleIconUnhover,
    layerGroupSelectedFrom,
    setLayerGroupSelectedFrom,
  ]);

  const selectedLayerValid = Object.values(layerGroups).some(
    (layerGroup) => layerGroup.name === selectedLayer,
  );

  return (
    <animated.div
      className={`layer-selection absolute z-[02] top-0 left-0 bg-open overflow-hidden rounded-br-lg ${
        isTouch ? `mobile ${isLayerSelectionExpanded ? "expanded" : ""}` : ""
      }`}
      onMouseEnter={handleIconHover}
      onMouseLeave={handleIconUnhover}
      ref={layerSelectionContainer}
      style={isTouch ? {} : { ...containerSprings }}
    >
      {Object.values(layerGroups)
        .filter((layerGroup) => {
          // On mobile, only show selected layer when collapsed
          if (isTouch && !isLayerSelectionExpanded) {
            return layerGroup.name === selectedLayer;
          }
          return true;
        })
        .map((layerGroup, index) => (
          <button
            key={index}
            className={`${
              !isTouch ? "hover:bg-shoreline" : ""
            } block text-left transition-[height] w-[25rem] ${
              layerGroup.name === selectedLayer && "bg-shoreline selected"
            } ${
              layerGroupSelectedFrom === LayerSelectionFrom.breadcrumb &&
              "bounce"
            }`}
            onClick={(e) => {
              if (isTouch && !isLayerSelectionExpanded) {
                e.stopPropagation();
                setIsLayerSelectionExpanded(true);
                return;
              }

              e.stopPropagation();
              setSelectedLayer(layerGroup.name);
              setLayerGroupSelectedFrom(LayerSelectionFrom.layerSelectionPanel);
              layerSelectedHandler({
                layerGroup: layerGroup.name,
              });
              if (isTouch) {
                setIsLayerSelectionExpanded(false);
              }
            }}
            ref={
              layerGroup.name === selectedLayer ||
              (!selectedLayerValid && index === 0)
                ? activeLayerButton
                : null
            }
          >
            <MenuItem
              iconSprings={iconSprings}
              name={layerGroup.name}
              IconComponent={layerGroup.IconComponent}
              description={layerGroup.shortDescription}
              showInfoIcon={isTouch}
              allowClickToExpand={false}
            />
          </button>
        ))}

      {/* Only show additional menu items when expanded on mobile */}
      {(!isTouch || isLayerSelectionExpanded) && (
        <>
          <div
            className={`bg-trench ${
              !isTouch ? "hover:bg-shoreline" : ""
            } block text-left transition-[height] w-[25rem]`}
          >
            <MenuItem
              iconSprings={iconSprings}
              name="Data"
              description={
                <>
                  <p>
                    {downloads.map((download) => (
                      <a
                        key={download.url}
                        href={download.url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex row items-center gap-2 hover:text-coral hover:stroke-coral hover:fill-coral"
                      >
                        {download.description}
                        {download.icon}
                      </a>
                    ))}
                  </p>
                  <h5>Links</h5>
                  <p>
                    <a
                      href="https://www.coastalresiliencelab.org/"
                      target="_blank"
                      rel="noreferrer"
                      className="hover:text-coral hover:stroke-coral hover:fill-coral"
                      onClick={() => {
                        linkOpenedHandler({
                          linkType: "website",
                          linkName:
                            "Discover the Center for Coastal Climate Resilience",
                          linkUrl: "https://www.coastalresiliencelab.org/",
                        });
                      }}
                    >
                      Discover the Center for Coastal Climate Resilience&nbsp;–›
                    </a>
                  </p>
                  <p>
                    <a
                      href="https://doi.org/10.1038/s41598-020-61136-6"
                      target="_blank"
                      rel="noreferrer"
                      className="hover:text-coral hover:stroke-coral hover:fill-coral"
                      onClick={() => {
                        linkOpenedHandler({
                          linkType: "paper",
                          linkName: "Read the scientific paper",
                          linkUrl: "https://doi.org/10.1038/s41598-020-61136-6",
                        });
                      }}
                    >
                      Read the publication&nbsp;–›
                    </a>
                  </p>
                </>
              }
              showInfoIcon={false}
            />
          </div>
          {!isTouch && (
            <div
              className={`bg-trench ${
                !isTouch ? "hover:bg-shoreline" : ""
              } block text-left transition-[height] w-[25rem]`}
            >
              <MenuItem
                iconSprings={iconSprings}
                name="Quick Explore"
                description={() => <QuickExplore />}
                IconComponent={() => (
                  <Icon
                    icon="material-symbols:explore-outline"
                    color="white"
                    className="w-full h-full"
                    height="1.3em"
                    width="1.3em"
                    style={{ transform: "scale(0.9)" }}
                  />
                )}
                showInfoIcon={false}
              />
            </div>
          )}
          {typeof window.google !== "undefined" && !isTouch && (
            <div
              className={`bg-trench ${
                !isTouch ? "hover:bg-shoreline" : ""
              } block text-left transition-[height] w-[25rem]`}
            >
              <MenuItem
                iconSprings={searchIconSprings}
                name="Search"
                className="search-menu-item"
                description={(updateHeight, hovered) => (
                  <SearchBar
                    setBounds={setBounds}
                    updateHeight={updateHeight}
                    hovered={hovered}
                  />
                )}
                IconComponent={() => (
                  <Icon
                    icon="material-symbols:search"
                    width="1.3em"
                    height="1.3em"
                    color="white"
                    className="w-full h-full"
                    style={{ transform: "scale(0.9)" }}
                    data-search-icon="true"
                  />
                )}
                showInfoIcon={false}
              />
            </div>
          )}
        </>
      )}
    </animated.div>
  );
};

export type UpdateHeightFunc = (height?: number) => void;

const MenuItem: React.FC<{
  iconSprings: {
    width: SpringValue<number>;
    height: SpringValue<number>;
  };
  name: string | React.ReactNode;
  IconComponent?: React.FC<React.SVGProps<SVGSVGElement>>;
  description:
    | string
    | React.ReactNode
    | ((updateHeight: UpdateHeightFunc, hovered: boolean) => React.ReactNode);
  showInfoIcon?: boolean;
  className?: string;
  allowClickToExpand?: boolean;
}> = ({
  iconSprings,
  name,
  IconComponent,
  description,
  showInfoIcon = true,
  className = "",
  allowClickToExpand = true,
}) => {
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const [hovered, setHovered] = useState(false);
  const [infoExpanded, setInfoExpanded] = useState(false);
  const [descriptionSprings, descriptionApi] = useSpring(() => ({
    from: { height: 0 },
  }));

  const { isTouch } = useMobileContext();

  const handleCopyHover = () => {
    // Only expand on hover for desktop (non-touch) devices
    if (!isTouch && !infoExpanded) {
      descriptionApi.start({ height: descriptionRef.current?.scrollHeight });
      setHovered(true);
    }
  };

  const handleCopyUnhover = () => {
    // Only collapse on unhover for desktop (non-touch) devices
    if (!isTouch && !infoExpanded) {
      setTimeout(() => {
        descriptionApi.start({ height: 0 });
        setHovered(false);
      }, 200);
    }
  };

  const handleInfoClick = () => {
    setInfoExpanded(!infoExpanded);
  };

  // Handle info expansion animation
  useEffect(() => {
    if (infoExpanded) {
      descriptionApi.start({ height: descriptionRef.current?.scrollHeight });
      setHovered(true);
    } else {
      descriptionApi.start({ height: 0 });
      setHovered(false);
    }
  }, [infoExpanded, descriptionApi]);

  const updateHeight = useCallback<UpdateHeightFunc>(
    (height) => {
      descriptionApi.start({
        height: height ? height : descriptionRef.current?.scrollHeight,
      });
    },
    [descriptionApi],
  );

  return (
    <div
      className={`${
        className === "search-menu-item"
          ? "flex items-center gap-3 px-3"
          : "grid grid-flow-col gap-3 px-3"
      } ${className}`}
    >
      <div
        className={`${
          className === "search-menu-item" ? "flex-shrink-0" : "self-start"
        } py-2`}
      >
        <animated.div style={{ ...iconSprings }}>
          {IconComponent ? (
            <IconComponent fill="white" />
          ) : (
            <LinkSvg
              color="white"
              width="83%"
              height="83%"
              className="mx-auto"
            />
          )}
        </animated.div>
      </div>
      <div
        onMouseEnter={handleCopyHover}
        onMouseLeave={handleCopyUnhover}
        onClick={
          allowClickToExpand && !showInfoIcon && description
            ? (e) => {
                e.stopPropagation();
                handleInfoClick();
              }
            : undefined
        }
        className={`${
          className === "search-menu-item" ? "flex-1" : "w-full"
        } py-2 text-white px-3 ${
          allowClickToExpand && !showInfoIcon && description
            ? "cursor-pointer"
            : ""
        }`}
      >
        <div className="flex items-center justify-between w-full">
          <h4>{name}</h4>
          {description && showInfoIcon && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleInfoClick();
              }}
              className="text-white/70 hover:text-white transition-colors"
              title="More information"
            >
              <Icon icon="material-symbols:info" width="1em" height="1em" />
            </button>
          )}
        </div>
        {description && typeof description === "string" ? (
          <animated.p
            className="w-full overflow-hidden"
            style={{ ...descriptionSprings }}
            ref={descriptionRef}
          >
            {description}
          </animated.p>
        ) : (
          <animated.div
            className="w-full overflow-hidden"
            style={{ ...descriptionSprings }}
            ref={descriptionRef}
          >
            {typeof description === "function"
              ? description(updateHeight, hovered)
              : description}
          </animated.div>
        )}
      </div>
    </div>
  );
};
